import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getCompliancePolicies, getCompliancePolicy } from '../api/internal/usersInternalApi';
import {
    Card, PageHead, Button, IconButton, Pill, PillDot, Input, Select, FieldLabel,
    EmptyState, Spinner, SpinnerContainer, Notice,
} from '../components/ui';
import {
    FilterBar, Muted, StatGrid, StatTile, AppLayout, AppRail, AppRow, BarreConformite,
    LienIcone, LegendeVerdicts, ChiffreCle, TitreSection, ParametreLigne, RatioBarre, PosteLigne,
} from '../components/security/security.styled';
import { libelleParametre } from '../components/security/complianceLabels';
import { formatDate } from '../components/security/severity';
import { downloadCsv } from '../components/security/csvDownload';

/* ═══════════════════════════════════════════════════════════════════
   Conformité Intune, stratégie par stratégie.

   La page répond à « où en est chaque stratégie sur le parc », là où la fiche
   appareil répond à « où en est ce poste ». Elle affiche systématiquement DEUX
   lectures, parce qu'elles divergent :

     · le VERDICT d'Intune, agrégé par stratégie et par poste ;
     · ce que disent les PARAMÈTRES, évalués un par un.

   Le verdict se périme entre deux check-ins — mesuré le 2026-08-27, sur 58
   stratégies marquées en échec, 44 avaient tous leurs paramètres conformes. La
   colonne d'écart est donc un indicateur à part entière, pas un détail : elle
   dit quand Intune n'a pas encore rattrapé la réalité du poste.
   ═══════════════════════════════════════════════════════════════════ */

const ETATS = [
    { key: 'compliant', label: 'Conforme', tone: 'ok' },
    { key: 'nonCompliant', label: 'Non conforme', tone: 'danger' },
    { key: 'error', label: 'Erreur', tone: 'warn' },
    { key: 'conflict', label: 'Conflit', tone: 'warn' },
    { key: 'notApplicable', label: 'Non applicable', tone: 'neutral' },
    { key: 'unknown', label: 'Inconnu', tone: 'neutral' },
];

const LIBELLE_ETAT = Object.fromEntries(ETATS.map((e) => [e.key, e.label]));
const TON_ETAT = Object.fromEntries(ETATS.map((e) => [e.key, e.tone]));

function EtatPill({ etat }) {
    return (
        <Pill $tone={TON_ETAT[etat] || 'neutral'}>
            <PillDot />
            {LIBELLE_ETAT[etat] || etat}
        </Pill>
    );
}

function pourcent(valeur, total) {
    if (!total) return 0;
    return (100 * valeur) / total;
}

/* ─────────────── Détail d'une stratégie ───────────────

   Lu de haut en bas, dans l'ordre des questions qu'on se pose :
     1. où en est la stratégie — la répartition des verdicts ;
     2. combien de postes faut-il traiter — LE chiffre, mis en avant ;
     3. sur quoi échouent-ils — les paramètres, en clair ;
     4. lesquels — les postes, qui s'ouvrent par défaut sur ceux à traiter.

   La première version empilait deux encarts d'avertissement AVANT toute
   donnée, portait des identifiants de 80 caractères dans un tableau à quatre
   colonnes large de 540 px, et listait les 644 postes d'une stratégie Windows
   dont 629 conformes. On ouvrait le panneau pour treize lignes, et il fallait
   les chercher. */

const PAR_PAGE = 50;

function PanneauStrategie({ policyId, resume, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [vue, setVue] = useState('aTraiter');
    const [etat, setEtat] = useState('');
    const [recherche, setRecherche] = useState('');
    const [limite, setLimite] = useState(PAR_PAGE);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setDetail(null);
        // Les filtres repartent à zéro d'une stratégie à l'autre : garder
        // « erreur » en changeant de stratégie donnerait un panneau vide qu'on
        // croirait cassé.
        setEtat('');
        setRecherche('');
        setLimite(PAR_PAGE);
        getCompliancePolicy(policyId, controller.signal)
            .then((res) => {
                if (!res) return;
                setDetail(res);
                // On ouvre sur les postes à traiter quand il y en a : c'est la
                // raison pour laquelle on a cliqué. Sinon, sur l'ensemble.
                setVue(res.counts.withFailingSetting > 0 ? 'aTraiter' : 'tous');
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [policyId]);

    const postes = useMemo(() => {
        if (!detail) return [];
        const terme = recherche.trim().toLowerCase();
        return detail.devices.filter((d) => {
            if (vue === 'aTraiter' && d.failingSettings.length === 0) return false;
            if (vue === 'tous' && etat && d.state !== etat) return false;
            if (!terme) return true;
            return (d.hostname || '').toLowerCase().includes(terme)
                || (d.upn || '').toLowerCase().includes(terme);
        });
    }, [detail, vue, etat, recherche]);

    if (loading) return <Card><SpinnerContainer><Spinner /></SpinnerContainer></Card>;
    if (!detail) {
        return (
            <Card>
                <Notice $tone="danger">
                    <i className="bx bx-error-circle" />
                    Chargement du détail impossible.
                </Notice>
            </Card>
        );
    }

    const { counts } = detail;
    const nomStrategie = detail.displayName;
    const libelle = (s) => libelleParametre(s.settingName, s.setting, nomStrategie);

    const exporter = () => {
        if (postes.length === 0) return;
        downloadCsv(
            `${nomStrategie || 'strategie'}_conformite.csv`,
            ['Stratégie', 'Poste', 'Porteur', 'Système', 'Rattachement', "Verdict d'Intune",
                'Paramètres évalués', 'Paramètres en échec', 'Détail des échecs', 'Valeurs constatées'],
            postes.map((d) => [
                nomStrategie || '',
                d.hostname || '',
                d.upn || '',
                [d.operatingSystem, d.operatingSystemVersion].filter(Boolean).join(' '),
                d.enrollmentType || '',
                LIBELLE_ETAT[d.state] || d.state,
                d.settingsEvaluated,
                d.failingSettings.length,
                d.failingSettings.map((s) => {
                    const nom = libelle(s);
                    return `${nom.libelle} (${nom.technique}) : ${LIBELLE_ETAT[s.state] || s.state}`;
                }).join(' | '),
                d.failingSettings.map((s) => s.currentValue).filter(Boolean).join(' | '),
            ])
        );
    };

    const erreurs = counts.error + counts.conflict;
    const visibles = postes.slice(0, limite);

    return (
        <Card $pad="16px">
            {/* ── En-tête ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Le nom n'est PAS tronqué : « [CNS] Windows - Check
                        Security App : SentinelOne » perdrait sa fin, qui est
                        justement ce qui la distingue de sa voisine MDE. */}
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.3 }}>{nomStrategie}</div>
                    <Muted style={{ fontSize: '0.72rem' }}>
                        {[
                            resume?.platforms?.join(', '),
                            `${counts.devices.toLocaleString('fr-FR')} appareils`,
                            `${detail.settings.length} paramètre${detail.settings.length > 1 ? 's' : ''}`,
                            resume?.lastSync && `relevé le ${formatDate(resume.lastSync)}`,
                        ].filter(Boolean).join(' · ')}
                    </Muted>
                </div>
                <IconButton $bordered onClick={onClose} title="Fermer le détail">
                    <i className="bx bx-x" />
                </IconButton>
            </div>

            {/* ── 1. Répartition des verdicts ── */}
            <div style={{ marginTop: '14px' }}>
                <BarreConformite $haute>
                    <span className="conforme" style={{ width: `${pourcent(counts.compliant, counts.devices)}%` }} />
                    <span className="nonConforme" style={{ width: `${pourcent(counts.nonCompliant, counts.devices)}%` }} />
                    <span className="erreur" style={{ width: `${pourcent(erreurs, counts.devices)}%` }} />
                    <span className="nonApplicable" style={{ width: `${pourcent(counts.notApplicable, counts.devices)}%` }} />
                </BarreConformite>
                <LegendeVerdicts>
                    <span><i className="conforme" /><b>{counts.compliant.toLocaleString('fr-FR')}</b> conformes</span>
                    {counts.nonCompliant > 0 && <span><i className="nonConforme" /><b>{counts.nonCompliant}</b> non conformes</span>}
                    {erreurs > 0 && <span><i className="erreur" /><b>{erreurs}</b> en erreur</span>}
                    {counts.notApplicable > 0 && <span><i className="nonApplicable" /><b>{counts.notApplicable}</b> non applicables</span>}
                </LegendeVerdicts>
            </div>

            {/* ── 2. Le chiffre qu'on vient chercher ──
                Les deux mises en garde — verdict contredit, poste jamais évalué —
                deviennent des précisions sous ce chiffre, au lieu de deux
                encarts qui repoussaient toute la donnée vers le bas. */}
            <ChiffreCle $tone={counts.withFailingSetting > 0 ? 'danger' : 'ok'}>
                {counts.withFailingSetting > 0 ? (
                    <>
                        <strong>{counts.withFailingSetting}</strong>
                        <span>poste{counts.withFailingSetting > 1 ? 's' : ''} à traiter</span>
                    </>
                ) : (
                    <span><i className="bx bx-check-circle" /> Aucun paramètre en échec</span>
                )}
                {(counts.disagreeing > 0 || counts.neverEvaluated > 0) && (
                    <small>
                        {counts.disagreeing > 0 && (
                            <>
                                {counts.disagreeing} verdict{counts.disagreeing > 1 ? 's' : ''} d'Intune
                                {' '}contredit{counts.disagreeing > 1 ? 's' : ''} par le détail des paramètres —
                                l'agrégat se périme entre deux remontées, le détail fait foi.
                            </>
                        )}
                        {counts.disagreeing > 0 && counts.neverEvaluated > 0 && ' '}
                        {counts.neverEvaluated > 0 && (
                            <>
                                {counts.neverEvaluated} poste{counts.neverEvaluated > 1 ? 's' : ''} sans
                                aucun paramètre évalué.
                            </>
                        )}
                    </small>
                )}
            </ChiffreCle>

            {/* ── 3. Les paramètres ── */}
            <TitreSection>Paramètres</TitreSection>
            {detail.settings.length === 0 ? (
                <Muted style={{ fontSize: '0.78rem' }}>
                    Aucun paramètre n'est remonté pour cette stratégie.
                </Muted>
            ) : (
                detail.settings.map((s) => {
                    const nom = libelle(s);
                    return (
                        <ParametreLigne key={s.setting} $echec={s.failing > 0}>
                            <div className="libelle">{nom.libelle}</div>
                            <div className="ratio">
                                <b>{s.failing}</b> / {s.evaluated.toLocaleString('fr-FR')}
                                <RatioBarre>
                                    <span style={{ width: `${pourcent(s.failing, s.evaluated)}%` }} />
                                </RatioBarre>
                            </div>
                            <div className="technique">
                                {nom.technique}
                                {nom.attendu && ` · attendu : ${nom.attendu}`}
                            </div>
                        </ParametreLigne>
                    );
                })
            )}

            {/* ── 4. Les postes ── */}
            <TitreSection>Appareils</TitreSection>
            <FilterBar style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                        $variant={vue === 'aTraiter' ? 'primary' : 'secondary'}
                        onClick={() => { setVue('aTraiter'); setLimite(PAR_PAGE); }}
                        disabled={counts.withFailingSetting === 0}
                    >
                        À traiter ({counts.withFailingSetting})
                    </Button>
                    <Button
                        $variant={vue === 'tous' ? 'primary' : 'secondary'}
                        onClick={() => { setVue('tous'); setLimite(PAR_PAGE); }}
                    >
                        Tous ({counts.devices.toLocaleString('fr-FR')})
                    </Button>
                </div>
                <Button
                    $variant="secondary"
                    onClick={exporter}
                    disabled={postes.length === 0}
                    style={{ marginLeft: 'auto' }}
                >
                    <i className="bx bxs-download" />
                    Exporter
                </Button>
            </FilterBar>
            <FilterBar style={{ marginBottom: '12px' }}>
                <Input
                    style={{ flex: 1, minWidth: '150px' }}
                    placeholder="Poste ou porteur..."
                    value={recherche}
                    onChange={(e) => { setRecherche(e.target.value); setLimite(PAR_PAGE); }}
                />
                {/* Le filtre par verdict n'a de sens que sur l'ensemble : sur
                    « À traiter », tous les postes ont déjà un échec. */}
                {vue === 'tous' && (
                    <Select value={etat} onChange={(e) => { setEtat(e.target.value); setLimite(PAR_PAGE); }}>
                        <option value="">Tous les verdicts</option>
                        {ETATS.filter((e) => counts[e.key] > 0).map((e) => (
                            <option key={e.key} value={e.key}>{e.label} ({counts[e.key]})</option>
                        ))}
                    </Select>
                )}
            </FilterBar>

            {postes.length === 0 ? (
                <EmptyState style={{ padding: '28px 16px' }}>
                    <i className="bx bx-search-alt" />
                    <h3>Aucun appareil</h3>
                    <p>Aucun appareil ne correspond à ces critères.</p>
                </EmptyState>
            ) : (
                <>
                    {visibles.map((d) => (
                        <PosteLigne key={d.deviceIdIntune || d.hostname} $echec={d.failingSettings.length > 0}>
                            <div className="tete">
                                <span className="nom">{d.hostname || 'Sans nom'}</span>
                                {(d.deviceIdAAD || d.hostname) && (
                                    <LienIcone
                                        $mini
                                        as={Link}
                                        to={`/Security/Devices?device=${encodeURIComponent(d.deviceIdAAD || d.hostname)}`}
                                        title={`Fiche de ${d.hostname}`}
                                        aria-label="Ouvrir la fiche appareil"
                                    >
                                        <i className="bx bx-shield-quarter" />
                                    </LienIcone>
                                )}
                                <span className="verdict"><EtatPill etat={d.state} /></span>
                            </div>
                            <div className="identite">
                                {[d.upn, [d.operatingSystem, d.operatingSystemVersion].filter(Boolean).join(' ')]
                                    .filter(Boolean).join(' · ') || '—'}
                            </div>

                            {d.failingSettings.length > 0 && (
                                <ul className="echecs">
                                    {d.failingSettings.map((s) => {
                                        const nom = libelle(s);
                                        return (
                                            <li key={s.setting} className={s.state === 'error' ? 'erreur' : undefined}>
                                                <i className={`bx ${s.state === 'error' ? 'bx-error' : 'bx-x-circle'}`} />
                                                <span>
                                                    {nom.libelle}
                                                    {s.currentValue && (
                                                        <> — constaté <span className="valeur">{s.currentValue}</span></>
                                                    )}
                                                    {s.currentValue && nom.attendu && (
                                                        <>, attendu <span className="valeur">{nom.attendu}</span></>
                                                    )}
                                                    {s.state === 'error' && s.errorDescription && (
                                                        <Muted> — {s.errorDescription}</Muted>
                                                    )}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            {d.disagrees && (
                                <div className="contredit">
                                    <i className="bx bx-info-circle" />{' '}
                                    {d.failingSettings.length > 0
                                        ? 'Intune le dit conforme, le détail des paramètres le contredit.'
                                        : "Intune le dit en échec, mais aucun paramètre n'est fautif."}
                                </div>
                            )}
                        </PosteLigne>
                    ))}

                    {postes.length > limite && (
                        <Button
                            $variant="secondary"
                            style={{ width: '100%', marginTop: '8px' }}
                            onClick={() => setLimite((n) => n + PAR_PAGE)}
                        >
                            Afficher {Math.min(PAR_PAGE, postes.length - limite)} de plus
                            <Muted> · {visibles.length} sur {postes.length.toLocaleString('fr-FR')}</Muted>
                        </Button>
                    )}
                </>
            )}
        </Card>
    );
}

/* ─────────────── La page ─────────────── */

export default function SecurityCompliance() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selection = searchParams.get('policy');

    const [search, setSearch] = useState('');
    const [platform, setPlatform] = useState('');
    const [failingOnly, setFailingOnly] = useState(false);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);

    const charger = useCallback((signal) => {
        setLoading(true);
        return getCompliancePolicies({ search, platform, failingOnly }, signal)
            .then((data) => { if (data) setResult(data); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [search, platform, failingOnly]);

    useEffect(() => {
        const controller = new AbortController();
        // Frappe au clavier : on laisse retomber avant d'interroger.
        const timer = setTimeout(() => charger(controller.signal), search ? 300 : 0);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [charger, search]);

    const strategies = result?.policies || [];
    const totaux = result?.totals;

    return (
        <>
            <PageHead>
                <div>
                    <h2>Conformité Intune</h2>
                    <p>
                        Suivi par stratégie : le verdict d'Intune, et ce que disent réellement les paramètres.
                        {result?.lastSync && ` Relevé le ${formatDate(result.lastSync)}.`}
                    </p>
                </div>
            </PageHead>

            {totaux && (
                <StatGrid>
                    <StatTile $tone="accent">
                        <div className="label">Stratégies</div>
                        <div className="value">{totaux.policies}</div>
                        <div className="hint">appliquées sur le parc</div>
                    </StatTile>
                    <StatTile $tone="accent">
                        <div className="label">Appareils suivis</div>
                        <div className="value">{totaux.devices.toLocaleString('fr-FR')}</div>
                        <div className="hint">soumis à au moins une stratégie</div>
                    </StatTile>
                    <StatTile $tone={totaux.devicesWithFailingSetting > 0 ? 'danger' : 'ok'}>
                        <div className="label">Postes à traiter</div>
                        <div className="value">{totaux.devicesWithFailingSetting.toLocaleString('fr-FR')}</div>
                        <div className="hint">au moins un paramètre en échec</div>
                    </StatTile>
                    <StatTile $tone={totaux.failingSettingRows > 0 ? 'warn' : 'ok'}>
                        <div className="label">Paramètres en échec</div>
                        <div className="value">{totaux.failingSettingRows.toLocaleString('fr-FR')}</div>
                        <div className="hint">toutes stratégies confondues</div>
                    </StatTile>
                </StatGrid>
            )}

            <AppLayout>
                <Card>
                    <FilterBar style={{ marginBottom: '14px' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <FieldLabel htmlFor="conf-search">Recherche</FieldLabel>
                            <Input
                                id="conf-search"
                                style={{ width: '100%', marginTop: '6px' }}
                                placeholder="Nom de stratégie..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div>
                            <FieldLabel htmlFor="conf-platform">Plateforme</FieldLabel>
                            <Select
                                id="conf-platform"
                                style={{ marginTop: '6px' }}
                                value={platform}
                                onChange={(e) => setPlatform(e.target.value)}
                            >
                                <option value="">Toutes</option>
                                {(result?.platforms || []).map((p) => <option key={p} value={p}>{p}</option>)}
                            </Select>
                        </div>
                        <label htmlFor="conf-failing">
                            <input
                                id="conf-failing"
                                type="checkbox"
                                checked={failingOnly}
                                onChange={(e) => setFailingOnly(e.target.checked)}
                            />
                            Avec des échecs seulement
                        </label>
                    </FilterBar>

                    {loading && !result ? (
                        <SpinnerContainer><Spinner /></SpinnerContainer>
                    ) : strategies.length === 0 ? (
                        <EmptyState>
                            <i className="bx bx-shield-quarter" />
                            <h3>Aucune stratégie</h3>
                            <p>Aucune stratégie de conformité ne correspond à ces critères.</p>
                        </EmptyState>
                    ) : (
                        strategies.map((s) => (
                            <AppRow
                                key={s.policyId}
                                $selected={selection === s.policyId}
                                onClick={() => setSearchParams(selection === s.policyId ? {} : { policy: s.policyId })}
                                title="Voir le détail de la stratégie"
                            >
                                <div className="body">
                                    <div className="name">{s.displayName}</div>
                                    <div className="meta">
                                        <span>{s.devices.toLocaleString('fr-FR')} appareils</span>
                                        <span>{s.platforms.join(', ') || 'plateforme inconnue'}</span>
                                        <span>{s.settingCount} paramètre{s.settingCount > 1 ? 's' : ''}</span>
                                        {s.devicesWithFailingSetting > 0 && (
                                            <span style={{ fontWeight: 700 }}>
                                                {s.devicesWithFailingSetting} poste{s.devicesWithFailingSetting > 1 ? 's' : ''} à traiter
                                            </span>
                                        )}
                                    </div>
                                    {/* Verdict d'Intune ventilé. Le gris n'est pas un
                                        échec : « non applicable » veut dire hors périmètre. */}
                                    <BarreConformite title={`${s.compliant} conformes · ${s.nonCompliant} non conformes · ${s.error + s.conflict} en erreur · ${s.notApplicable} non applicables`}>
                                        <span className="conforme" style={{ width: `${pourcent(s.compliant, s.devices)}%` }} />
                                        <span className="nonConforme" style={{ width: `${pourcent(s.nonCompliant, s.devices)}%` }} />
                                        <span className="erreur" style={{ width: `${pourcent(s.error + s.conflict, s.devices)}%` }} />
                                        <span className="nonApplicable" style={{ width: `${pourcent(s.notApplicable, s.devices)}%` }} />
                                    </BarreConformite>
                                </div>
                                <div className="count">
                                    {s.devicesWithFailingSetting > 0
                                        ? <Pill $tone="danger"><PillDot />{s.devicesWithFailingSetting}</Pill>
                                        : <Pill $tone="ok">conforme</Pill>}
                                    {/* L'écart entre le verdict et le détail : il dit
                                        qu'Intune n'a pas encore rattrapé la réalité. */}
                                    {s.disagreement !== 0 && (
                                        <Muted style={{ display: 'block', fontSize: '0.7rem', marginTop: '3px' }}>
                                            verdict {s.disagreement > 0 ? '+' : ''}{s.disagreement}
                                        </Muted>
                                    )}
                                </div>
                            </AppRow>
                        ))
                    )}
                </Card>

                {selection && (
                    <AppRail>
                        <PanneauStrategie
                            policyId={selection}
                            resume={strategies.find((st) => st.policyId === selection)}
                            onClose={() => setSearchParams({})}
                        />
                    </AppRail>
                )}
            </AppLayout>
        </>
    );
}
